import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, Stack, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import moment from 'moment'
import {
  ExpenseModalProps,
  useAccountsPayableContext
} from 'templates/AccountsPayable/context'
import * as yup from 'yup'

import { DatePicker } from 'components/DatePicker'
import IconContainer from 'components/IconContainer/IconContainer'
import UploadComponent from 'components/UploadComponent'
import { FileParams } from 'components/UploadComponent/FileList'

import { currency, formatCentsToSendToBackEnd, onlyNumber } from 'utils'
import { MAX_RECEIPTS } from 'utils/attachment-limits'
import { toastFirstError } from 'utils/yupResolverWithToast'

import SourceAutocomplete from '../ModalNewExpense/components/autocompletes/SourceAutocomplete'

interface ModalMarkAsPaidProps {
  isOpen: boolean
  handleClose: Dispatch<SetStateAction<ExpenseModalProps>>
  uuid: string
}

// A data chega formatada em pt-BR ("15/09/2026"), porque é assim que
// constructorAccountsPayable entrega o dueDate para as telas. `moment` sem máscara não
// parseia esse formato: devolvia Invalid Date, o yup.date().required() reprovava e o
// campo abria em vermelho com o submit travado (SC-10785). A máscara vem primeiro; o
// fallback ISO cobre quem já passa a data crua da API.
export const parseModalDate = (value?: string | null) => {
  if (!value) return undefined

  const parsed = moment(value, ['DD/MM/YYYY', moment.ISO_8601], true)

  return parsed.isValid() ? parsed.toDate() : undefined
}

// Data do PAGAMENTO, não do vencimento: o DatePicker tem `maxDate={new Date()}` porque
// não se dá baixa no futuro. Usar o vencimento como valor inicial abria o campo já
// inválido em toda despesa a vencer (a do SC-10785 vencia em 15/09 e o campo nascia
// vermelho). O vencimento só serve de sugestão quando é passado — quem paga em atraso
// costuma lançar a data real, e quem antecipa ajusta para o dia do pagamento.
export const defaultPaymentDate = (dueDate?: string | null) => {
  const parsed = parseModalDate(dueDate)
  const today = moment().startOf('day')

  if (!parsed || moment(parsed).isAfter(today)) return today.toDate()

  return parsed
}

export const schemaModal = yup.object({
  dueDate: yup.date().nullable().required('Data de vencimento obrigatória.'),
  amountPaid: yup
    .string()
    .typeError('Valor obrigatório.')
    .nullable()
    .required('Valor obrigatório.'),
  documentUuids: yup.array().of(yup.string().required()).notRequired(),
  source: yup.object({
    value: yup.string().notRequired().nullable(),
    label: yup.string().notRequired().nullable()
  })
})

export type SchemaType = yup.InferType<typeof schemaModal>

const ModalMarkAsPaid = ({
  handleClose,
  uuid,
  isOpen
}: ModalMarkAsPaidProps) => {
  const { setReloadTable, reloadTable, expenseMarkAsPaidModal } =
    useAccountsPayableContext()
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting }
  } = useForm<SchemaType>({
    defaultValues: {
      documentUuids: [],
      dueDate: defaultPaymentDate(expenseMarkAsPaidModal.dueDate),
      amountPaid: currency(expenseMarkAsPaidModal?.amount || '')
        .replace(/\./g, '')
        .replace(',', '.')
    },
    resolver: yupResolver(schemaModal)
  })
  const [files, setFiles] = useState<FileParams[]>([])
  const uploadRef = useRef<UploadComponent>({} as UploadComponent)

  const handleSubmitForm: SubmitHandler<SchemaType> = async (dataForm) => {
    const data = {
      accounts_payable_source_uuid: dataForm.source.value,
      date_paid: moment(dataForm.dueDate).format('YYYY-MM-DD'),
      amount_paid: formatCentsToSendToBackEnd(onlyNumber(dataForm.amountPaid)),
      ...(dataForm.documentUuids?.length && {
        document_uuids: dataForm.documentUuids
      })
    }
    // O await é obrigatório: o handleSubmit segura o isSubmitting (e o botão) até a
    // resposta. Sem ele o duplo clique mandava dois POST /pay e o 2º voltava
    // 'Conta a pagar já paga.', com um toast de sucesso e um de erro.
    try {
      await toast.promise(
        async () => {
          const response = await DataService({
            type: 'POST',
            url: `/v1/accounts_payables/${uuid}/pay`,
            data
          })
          setReloadTable(!reloadTable)

          if (response.error) {
            throw new Error(
              response.error && (response.detail_error || response.message)
            )
          } else {
            handleClose({ isOpen: false, uuid: '' })
            return response.data.message
          }
        },
        {
          pending: 'Marcando como paga...',
          success: {
            render({ data }) {
              return `${data}`
            }
          },
          error: {
            render({ data }) {
              return `${
                (data as any)?.message || 'Houve um erro ao marcar como paga.'
              }`
            }
          }
        }
      )
    } catch {
      // erro já exibido pelo toast.promise
    }
  }
  const handleClickClose = () => {
    handleClose({ isOpen: false, uuid: '' })
    reset()
  }

  useEffect(() => {
    if (files) {
      setValue('documentUuids', files.map((file) => file.id).filter(Boolean))
    }
  }, [files])

  return (
    <Modal
      handleClose={() => handleClickClose()}
      open={isOpen}
      width="410px"
      height="auto"
    >
      <ModalBody
        component="form"
        onSubmit={handleSubmit(handleSubmitForm, toastFirstError)}
      >
        <ModalHeader onClose={handleClickClose}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="faCoins" />
            <Typography variant="mdBold">Marcar como paga</Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          <Grid container gap="15px">
            <Grid xs={12} sm={12} lg={12} item>
              <Controller
                control={control}
                name="source"
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => {
                  const errorTyped = error as any
                  return (
                    <SourceAutocomplete
                      key={value?.value || 'source'}
                      value={
                        value as {
                          label: string
                          value: string
                        }
                      }
                      onChange={(
                        _: any,
                        value: { label: string; value: string }
                      ) => {
                        const option = value as {
                          label: string
                          value: string
                        }
                        onChange(option ?? { value: '', label: '' })
                      }}
                      errorMessage={errorTyped?.label?.message}
                      setValue={setValue}
                    />
                  )
                }}
              />
            </Grid>
            <Grid xs={12} sm={12} lg={12} item>
              <Controller
                control={control}
                name="dueDate"
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => {
                  return (
                    <DatePicker
                      helperText={error?.message}
                      error={!!error?.message}
                      label="Data do pagamento"
                      onChange={onChange}
                      renderInput={(props: any) => (
                        <Textfield fullWidth {...props} />
                      )}
                      maxDate={new Date()}
                      toolbarPlaceholder="Escolha a data de vencimento"
                      value={value ? value : null}
                    />
                  )
                }}
              />
            </Grid>
            <Grid xs={12} sm={12} lg={12} item>
              <Controller
                control={control}
                name="amountPaid"
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => (
                  <Textfield
                    customStartAdornment={<Typography mr="6px">R$</Typography>}
                    helperText={error?.message}
                    error={!!error?.message}
                    label="Valor pago"
                    placeholder="Preencha o valor:"
                    mask="money"
                    name="amountPaid"
                    onChange={onChange}
                    startAdornment={true}
                    value={value}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} sm={12} lg={12} item>
              <UploadComponent
                variant="small"
                docType="account_payable"
                type="accounts_payables"
                maxFiles={MAX_RECEIPTS}
                subTitle={`Clique aqui para inserir o comprovante (até ${MAX_RECEIPTS}).`}
                title="Anexar comprovantes"
                ref={uploadRef}
                setStateExternal={setFiles}
                accept={{
                  'application/pdf': ['.pdf'],
                  'application/vnd.oasis.opendocument.text': ['.odt'],
                  'application/msword': ['.doc'],
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    ['.docx'],
                  'image/jpeg': ['.jpeg', '.jpg'],
                  'image/png': ['.png'],
                  'image/webp': ['.webp']
                }}
              />
            </Grid>
          </Grid>
        </ModalContent>

        <ModalFooter>
          <Button
            type="submit"
            variantButton="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Marcar como paga
          </Button>
        </ModalFooter>
      </ModalBody>
    </Modal>
  )
}

export default ModalMarkAsPaid
